/**
 * Slide design metadata (theme, background studio, transition).
 *
 * Slides are stored as an array of canvas elements in presentation_contents.data.
 * To stay backward compatible with that array (and with the mobile app, which
 * ignores unknown element types), the design lives in one hidden element:
 *
 *   { type: "SLIDE_DESIGN", id: "__slide_design__", design: {...} }
 *
 * Old slides simply have no such element and fall back to the defaults below.
 * Because the element sits inside canvasData, editor undo/redo and autosave
 * cover design changes with no extra plumbing.
 */

export const DESIGN_ELEMENT_TYPE = "SLIDE_DESIGN";
export const DESIGN_ELEMENT_ID = "__slide_design__";
export const DESIGN_VERSION = 1;

export const DEFAULT_TRANSITION = Object.freeze({
  type: "fade",
  duration: 650,
  easing: "smooth",
});

export const MIN_TRANSITION_MS = 200;
export const MAX_TRANSITION_MS = 2000;

export const DEFAULT_BACKGROUND_ADJUST = Object.freeze({
  brightness: 100,
  blur: 0,
  opacity: 100,
  overlay: "none",
  overlayStrength: 35,
});

export const isDesignElement = (item) =>
  Boolean(item) && (item.type === DESIGN_ELEMENT_TYPE || item.id === DESIGN_ELEMENT_ID);

/** Elements that the canvas actually draws (everything except design metadata). */
export const getRenderableElements = (data) =>
  (Array.isArray(data) ? data : []).filter((item) => item && !isDesignElement(item));

export const clampDuration = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_TRANSITION.duration;
  return Math.round(Math.min(MAX_TRANSITION_MS, Math.max(MIN_TRANSITION_MS, n)));
};

const normalizeTransition = (transition) => {
  if (!transition || typeof transition !== "object") return null;
  return {
    type: typeof transition.type === "string" ? transition.type : DEFAULT_TRANSITION.type,
    duration: clampDuration(transition.duration ?? DEFAULT_TRANSITION.duration),
    easing: typeof transition.easing === "string" ? transition.easing : DEFAULT_TRANSITION.easing,
  };
};

const normalizeBackground = (background) => {
  if (!background || typeof background !== "object" || !background.kind) return null;
  return {
    ...background,
    adjust: { ...DEFAULT_BACKGROUND_ADJUST, ...(background.adjust || {}) },
  };
};

/** Read the design for a slide's canvas data. Never returns null. */
export const getSlideDesign = (data) => {
  const element = (Array.isArray(data) ? data : []).find(isDesignElement);
  const raw = element?.design && typeof element.design === "object" ? element.design : {};
  return {
    version: DESIGN_VERSION,
    themeId: typeof raw.themeId === "string" ? raw.themeId : null,
    background: normalizeBackground(raw.background),
    transition: normalizeTransition(raw.transition),
  };
};

/** True when the slide carries any design metadata at all. */
export const hasSlideDesign = (data) =>
  (Array.isArray(data) ? data : []).some(isDesignElement);

/** The transition used when entering a slide (falls back to theme, then default). */
export const resolveTransition = (design, theme) =>
  design?.transition || theme?.transition || DEFAULT_TRANSITION;

/**
 * Return a new canvas data array with the design patched in.
 * The design element is kept last so layer ops that look at index 0
 * (background image handling) keep working unchanged.
 */
export const withSlideDesign = (data, patch) => {
  const list = Array.isArray(data) ? data : [];
  const current = getSlideDesign(list);
  const nextDesign = { ...current, ...(typeof patch === "function" ? patch(current) : patch) };
  const rest = list.filter((item) => !isDesignElement(item));
  return [
    ...rest,
    { type: DESIGN_ELEMENT_TYPE, id: DESIGN_ELEMENT_ID, design: nextDesign },
  ];
};
